import React, { useState, useRef, useEffect } from 'react';
import EmailEditor from './EmailEditor';
import EmailChipsInput from './EmailChipsInput';

const EmailCompose = ({ onSend, onCancel, userEmail, userName, replyTo }) => {
  // Helper function to extract email from "Name <email@domain.com>" format
  const extractEmail = (emailString) => {
    if (!emailString) return '';
    // Check if it's in "Name <email>" format
    const match = emailString.match(/<([^>]+)>/);
    if (match) {
      return match[1].trim();
    }
    // Otherwise return as is (it's probably just the email)
    return emailString.trim();
  };

  const [emailData, setEmailData] = useState({
    to: '',
    cc: '',
    bcc: '',
    subject: '',
    body: '',
    fromEmail: '', // Add fromEmail field
    attachments: [],
    inlineImages: [] // Track inline images separately
  });
  
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [sending, setSending] = useState(false);
  const [availableEmails, setAvailableEmails] = useState([]);
  const [loadingEmails, setLoadingEmails] = useState(false);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const editorRef = useRef(null);

  // API base URL based on environment
  const API_BASE_URL = process.env.NODE_ENV === 'production'
    ? process.env.REACT_APP_VERCEL_API_URL || 'https://api.veridicus.com.br'
    : process.env.REACT_APP_LOCAL_API_URL || 'http://localhost:5000';

  // Fetch available emails on mount
  useEffect(() => {
    const fetchAvailableEmails = async () => {
      setLoadingEmails(true);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_BASE_URL}/api/emails/aliases/list`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          const emails = [];
          
          // Add current user's email
          if (data.currentUser) {
            emails.push({
              email: data.currentUser.email,
              name: data.currentUser.nomeCompleto || data.currentUser.username,
              isMain: true
            });
          }
          
          // Add alias emails
          if (data.aliases && data.aliases.length > 0) {
            data.aliases.forEach(alias => {
              emails.push({
                email: alias.email,
                name: alias.nomeCompleto || alias.username,
                isMain: false
              });
            });
          }
          
          setAvailableEmails(emails);
          
          // Set default fromEmail to user's main email
          if (emails.length > 0 && !emailData.fromEmail) {
            setEmailData(prev => ({ 
              ...prev, 
              fromEmail: emails[0].email
            }));
          }
        }
      } catch (error) {
        console.error('Error fetching available emails:', error);
      } finally {
        setLoadingEmails(false);
      }
    };
    
    fetchAvailableEmails();
  }, [API_BASE_URL, emailData.fromEmail]);

  useEffect(() => {
    // Set reply data if replying to an email
    if (replyTo && replyTo.from) {
      const replySubject = replyTo.subject && !replyTo.subject.startsWith('Re: ') 
        ? `Re: ${replyTo.subject}` 
        : replyTo.subject || 'Re: (sem assunto)';
      
      // Extract just the email address from the from field
      const recipientEmail = extractEmail(replyTo.from);
        
      setEmailData(prev => ({
        ...prev,
        to: recipientEmail,
        subject: replySubject,
        body: `<br><br><div style="border-left: 3px solid #ccc; padding-left: 10px; margin-left: 10px;">
          Em ${new Date(replyTo.date).toLocaleString('pt-BR')}, ${replyTo.from} escreveu:<br>
          ${replyTo.body}
        </div>`
      }));
    }
  }, [replyTo]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEmailData(prev => ({ ...prev, [name]: value }));
  };

  const handleBodyChange = (content) => {
    setEmailData(prev => ({ ...prev, body: content }));
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setEmailData(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...files]
    }));
  };

  const removeAttachment = (index) => {
    setEmailData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleImageInsert = (e) => {
    const files = Array.from(e.target.files);
    
    files.forEach((file, fileIndex) => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (event) => {
          // Simply use the index in the inlineImages array
          const inlineImageIndex = emailData.inlineImages.length + fileIndex;
          
          // Insert into editor with inline image index
          if (editorRef.current) {
            editorRef.current.insertImage(event.target.result, `inline-${inlineImageIndex}`);
          }
        };
        reader.readAsDataURL(file);
        
        // Add to inline images array
        setEmailData(prev => ({
          ...prev,
          inlineImages: [...prev.inlineImages, file]
        }));
      }
    });
    
    // Reset input
    e.target.value = '';
  };

  const removeInlineImage = (index) => {
    setEmailData(prev => ({
      ...prev,
      inlineImages: prev.inlineImages.filter((_, i) => i !== index)
    }));
  };

  const handleSend = async () => {
    if (!emailData.to) {
      alert('Por favor, adicione um destinatário');
      return;
    }

    setSending(true);
    try {
      // Check if we have any attachments or inline images
      const hasAttachments = emailData.attachments.length > 0 || emailData.inlineImages.length > 0;
      
      if (hasAttachments) {
        // Use FormData for emails with attachments
        const formData = new FormData();
        formData.append('to', emailData.to);
        formData.append('cc', emailData.cc);
        formData.append('bcc', emailData.bcc);
        formData.append('subject', emailData.subject);
        formData.append('body', emailData.body);
        formData.append('fromEmail', emailData.fromEmail);
        
        // Add regular attachments
        emailData.attachments.forEach((file) => {
          formData.append('attachments', file);
        });
        
        // Add inline images separately
        emailData.inlineImages.forEach((file, index) => {
          formData.append('inlineImages', file);
        });
        
        await onSend(formData, true); // Pass flag indicating FormData
      } else {
        // Use regular JSON for simple emails without attachments
        await onSend({
          to: emailData.to,
          cc: emailData.cc,
          bcc: emailData.bcc,
          subject: emailData.subject,
          body: emailData.body,
          fromEmail: emailData.fromEmail
        }, false); // Pass flag indicating JSON
      }
    } catch (error) {
      console.error('Error sending email:', error);
    }
    setSending(false);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="email-compose">
      <div className="compose-header">
        <h3>Nova mensagem</h3>
        <button className="close-btn" onClick={onCancel}>
          <i className="fas fa-times"></i>
        </button>
      </div>
      
      <div className="compose-form">
        {/* From field */}
        {availableEmails.length > 1 && (
          <div className="form-field" data-label="De">
            <select
              name="fromEmail"
              value={emailData.fromEmail}
              onChange={handleInputChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                fontSize: '14px',
                backgroundColor: 'white',
                color: '#333'
              }}
              disabled={loadingEmails}
            >
              {loadingEmails ? (
                <option>Carregando emails...</option>
              ) : (
                availableEmails.map((email, index) => (
                  <option key={index} value={email.email}>
                    {email.name} &lt;{email.email}&gt; {email.isMain ? '(Principal)' : '(Alias)'}
                  </option>
                ))
              )}
            </select>
          </div>
        )}
        
        <div className="form-field email-field-with-actions" data-label="Para">
          <EmailChipsInput
            name="to"
            value={emailData.to}
            onChange={handleInputChange}
            placeholder="Destinatários"
          />
          <div className="field-actions">
            <button 
              type="button"
              className="cc-bcc-btn"
              onClick={() => setShowCc(!showCc)}
            >
              Cc
            </button>
            <button 
              type="button"
              className="cc-bcc-btn"
              onClick={() => setShowBcc(!showBcc)}
            >
              Cco
            </button>
          </div>
        </div>
        
        {showCc && (
          <div className="form-field" data-label="Cc">
            <EmailChipsInput
              name="cc"
              value={emailData.cc}
              onChange={handleInputChange}
              placeholder="Adicionar destinatários"
            />
          </div>
        )}
        
        {showBcc && (
          <div className="form-field" data-label="Cco">
            <EmailChipsInput
              name="bcc"
              value={emailData.bcc}
              onChange={handleInputChange}
              placeholder="Adicionar destinatários"
            />
          </div>
        )}
        
        <div className="form-field" data-label="Assunto">
          <input
            type="text"
            name="subject"
            value={emailData.subject}
            onChange={handleInputChange}
            placeholder="Assunto"
          />
        </div>
        
        <div className="compose-body">
          <EmailEditor 
            ref={editorRef}
            content={emailData.body}
            onChange={handleBodyChange}
          />
        </div>
        
        {(emailData.attachments.length > 0 || emailData.inlineImages.length > 0) && (
          <div className="attachments-preview">
            {emailData.attachments.map((file, index) => (
              <div key={`att-${index}`} className="attachment-preview">
                <i className="fas fa-paperclip"></i>
                <span>{file.name} ({formatFileSize(file.size)})</span>
                <button onClick={() => removeAttachment(index)}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
            ))}
            {emailData.inlineImages.map((file, index) => (
              <div key={`img-${index}`} className="attachment-preview inline-image-preview">
                <i className="fas fa-image" style={{ color: '#3E5F56' }}></i>
                <span>{file.name} ({formatFileSize(file.size)}) - Inline</span>
                <button onClick={() => removeInlineImage(index)}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
            ))}
          </div>
        )}
        
        <div className="compose-actions">
          <div className="left-actions">
            <button 
              className="send-btn"
              onClick={handleSend}
              disabled={sending}
            >
              {sending ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Enviando...
                </>
              ) : (
                <>
                  <i className="fas fa-paper-plane"></i>
                  Enviar
                </>
              )}
            </button>
            
            <div className="compose-tools">
              <button 
                className="tool-btn"
                onClick={() => fileInputRef.current?.click()}
                title="Anexar arquivos"
              >
                <i className="fas fa-paperclip"></i>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              
              <button className="tool-btn" title="Inserir link">
                <i className="fas fa-link"></i>
              </button>
              
              <button className="tool-btn" title="Inserir emoji">
                <i className="fas fa-smile"></i>
              </button>
              
              <button className="tool-btn" title="Inserir arquivos do Google Drive">
                <i className="fab fa-google-drive"></i>
              </button>
              
              <button 
                className="tool-btn"
                onClick={() => imageInputRef.current?.click()}
                title="Inserir foto"
              >
                <i className="fas fa-image"></i>
              </button>
              <input
                ref={imageInputRef}
                type="file"
                multiple
                accept="image/*"
                onChange={handleImageInsert}
                style={{ display: 'none' }}
              />
              
              <button className="tool-btn" title="Modo confidencial">
                <i className="fas fa-lock"></i>
              </button>
            </div>
          </div>
          
          <button className="delete-draft-btn" onClick={onCancel} title="Descartar rascunho">
            <i className="fas fa-trash"></i>
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailCompose;